import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { ApplyForm } from "@/components/ApplyForm";

const DISMISS_KEY = "srr_apply_dismissed";

// Pops the application form over the page: on CTA click, and once after 10s.
export const ApplyModal = ({ open, onOpenChange, autoDelayMs = 10000 }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    const t = setTimeout(() => onOpenChange(true), autoDelayMs);
    return () => clearTimeout(t);
  }, [autoDelayMs, onOpenChange]);

  useEffect(() => {
    if (open) setMounted(true);
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const close = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    onOpenChange(false);
  };

  if (!mounted) return null;

  return (
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-[100] transition-opacity duration-300 ${
        open ? "opacity-100" : "opacity-0 invisible pointer-events-none"
      }`}
      data-testid="apply-modal"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        onClick={close}
        data-testid="apply-modal-overlay"
      />
      <div className="absolute inset-0 overflow-y-auto overscroll-contain">
        <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Apply for access"
            className={`relative w-full max-w-lg bg-[#0B0C0D] border border-zinc-800 rounded-sm shadow-2xl transition-all duration-300 ${
              open ? "translate-y-0 scale-100" : "translate-y-3 scale-[0.98]"
            }`}
          >
            <div className="flex items-start gap-3 border-b border-zinc-900 px-5 py-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-500">
                  Invite only
                </p>
                <h2 className="mt-1.5 text-base md:text-lg font-heading font-semibold tracking-tight">
                  Apply for access
                </h2>
                <p className="mt-1 text-xs text-zinc-500 leading-relaxed">
                  Three quick screens, then pick a slot for your 1-1 call.
                </p>
              </div>
              <div className="flex-1" />
              <button
                type="button"
                onClick={close}
                data-testid="apply-modal-close"
                aria-label="Close"
                className="shrink-0 -mt-1 -mr-1 h-9 w-9 inline-flex items-center justify-center rounded-sm border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-600 transition-colors duration-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 sm:p-5">
              <ApplyForm bare />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyModal;
